import * as React from 'react'
import { Circle } from 'react-konva'
import type Konva from 'konva'
import { ChartScanActivity } from '@/lib/api/company/chartScan'
import {
  clockMinute,
  moveActivityStart,
} from '@/modules/imports/container/scan-discs-popup-container/editor/chartDay'
import { angleRadToMinute, minuteToAngleRad } from './discPolar'

type Props = {
  activities: ChartScanActivity[]
  cx: number
  cy: number
  radius: number
  hoveredActivityId: number | null
  selectedActivityIds?: Set<number>
  // sameDrag: a further step of the drag that made the previous change (one undo step).
  onActivitiesChange: (next: ChartScanActivity[], sameDrag: boolean) => void
  onHoverActivity?: (id: number | null, fromCanvas: boolean) => void
  setCursor: (cursor: string) => void
}

// WPF TachographChartActivityItemControl: the highlighted activity shows a point at each end;
// dragging it moves the boundary shared with the neighbour.
export function ActivityBoundaryHandles({
  activities,
  cx,
  cy,
  radius,
  hoveredActivityId,
  selectedActivityIds,
  onActivitiesChange,
  onHoverActivity,
  setCursor,
}: Props) {
  const [draggedIndex, setDraggedIndex] = React.useState<number | null>(null)
  const changedInDrag = React.useRef(false)
  // Drag events can outrun the render, so the last emitted list is kept here.
  const latest = React.useRef(activities)
  latest.current = activities

  const count = activities.length
  if (count < 2) return null

  const isHighlighted = (a: ChartScanActivity) =>
    hoveredActivityId === a.activityId ||
    (selectedActivityIds?.has(a.activityId) ?? false)

  return (
    <>
      {activities.map((current, index) => {
        const previous = activities[(index - 1 + count) % count]
        const minute = clockMinute(current.startTime)
        if (clockMinute(previous.endTime) !== minute) return null
        if (
          draggedIndex !== index &&
          !isHighlighted(current) &&
          !isHighlighted(previous)
        ) {
          return null
        }
        return (
          <BoundaryHandle
            key={`handle-${index}`}
            cx={cx}
            cy={cy}
            radius={radius}
            minute={minute}
            onEnter={() => {
              setCursor('grab')
              // Leaving the wedge clears the hover, which would take this handle away.
              onHoverActivity?.(
                hoveredActivityId === previous.activityId
                  ? previous.activityId
                  : current.activityId,
                true,
              )
            }}
            onLeave={() => {
              setCursor('default')
              onHoverActivity?.(null, true)
            }}
            onDragStart={() => {
              changedInDrag.current = false
              setDraggedIndex(index)
              setCursor('grabbing')
            }}
            onDragEnd={() => {
              setDraggedIndex(null)
              setCursor('grab')
            }}
            onDrag={(pointerMinute) => {
              const next = moveActivityStart(
                latest.current,
                index,
                pointerMinute,
              )
              if (next) {
                latest.current = next
                onActivitiesChange(next, changedInDrag.current)
                changedInDrag.current = true
              }
              return clockMinute(latest.current[index].startTime)
            }}
          />
        )
      })}
    </>
  )
}

function BoundaryHandle({
  cx,
  cy,
  radius,
  minute,
  onEnter,
  onLeave,
  onDragStart,
  onDragEnd,
  onDrag,
}: {
  cx: number
  cy: number
  radius: number
  minute: number
  onEnter: () => void
  onLeave: () => void
  onDragStart: () => void
  onDragEnd: () => void
  // Gets the minute under the pointer, returns the minute the boundary ended up at.
  onDrag: (pointerMinute: number) => number
}) {
  const at = (m: number) => {
    const angle = minuteToAngleRad(m)
    return {
      x: cx + radius * Math.cos(angle),
      y: cy + radius * Math.sin(angle),
    }
  }
  const position = at(minute)
  const ref = React.useRef<Konva.Circle>(null)
  // react-konva skips props equal to the last render, so a dragged node is put back by hand.
  React.useLayoutEffect(() => {
    ref.current?.position(position)
  })
  return (
    <Circle
      ref={ref}
      x={position.x}
      y={position.y}
      radius={6}
      hitStrokeWidth={10}
      fill="#ffffff"
      stroke="#1e293b"
      strokeWidth={1.5}
      draggable
      // The stage is draggable when zoomed in; keep it from panning along.
      onMouseDown={(e) => {
        e.cancelBubble = true
      }}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      onDragStart={(e) => {
        e.cancelBubble = true
        onDragStart()
      }}
      onDragMove={(e) => {
        e.cancelBubble = true
        const node = e.target
        const pointerMinute = angleRadToMinute(
          Math.atan2(node.y() - cy, node.x() - cx),
        )
        node.position(at(onDrag(pointerMinute)))
      }}
      onDragEnd={(e) => {
        e.cancelBubble = true
        onDragEnd()
      }}
    />
  )
}
